/**
 * Tile mesh provider with a Promise-based API. Runs either synchronously on
 * the main thread (no worker provided) or delegates to a Web Worker via
 * {@link useWorker}.
 *
 * Angular CLI / esbuild does not process `new Worker(new URL(..., import.meta.url))`
 * across npm workspace package boundaries. Workers must be instantiated in the
 * Angular app via a thin shim file + `tsconfig.worker.json`; call `useWorker()`
 * after `init()` to activate the worker path.
 *
 * Cost on the main thread (Iceland @ 1024 res, 250 m tiles):
 *   - Initial 81-tile inner ring: ~50–100 ms during the existing loading screen
 *   - Per-tile-crossing (≤ 9 new LOD0 tiles): ~5 ms, absorbed by frame budget
 */

import {
  generateTileMesh,
  TileBounds,
  TileMeshConfig,
  TileMeshOutput,
  HeightSampleFn,
  HolePolygon,
} from './TileMeshGenerator';
import { HeightmapGrid, HeightmapHeightField } from '../heightfield/HeightmapHeightField';
import { NoiseHeightField } from '../heightfield/NoiseHeightField';
import {
  TerrainWorkerGenerateResult,
  TerrainWorkerMessage,
} from '../../workers/TerrainGenWorker';

/** Heightmap source dispatch (matches the manifest's optional heightmap PNG). */
export interface MeshHeightmapSource {
  kind: 'heightmap';
  pixels: Uint8Array;
  width: number;
  height: number;
  amplitude: number;
  worldMinX: number;
  worldMinZ: number;
  worldWidth: number;
  worldDepth: number;
  /**
   * Optional sculpt-delta layer composed on top of the base. R8 signed-shift
   * (byte 128 = 0). Absent → no composition (zero-cost base-only path).
   */
  sculptPixels?: Uint8Array;
  sculptWidth?: number;
  sculptHeight?: number;
  sculptAmplitude?: number;
}

/** Noise source dispatch (used when scene has no heightmap PNG). */
export interface MeshNoiseSource {
  kind: 'noise';
  octaves: number;
  baseFrequency: number;
  amplitude: number;
  falloffShape: string;
  worldWidth: number;
  worldDepth: number;
}

export interface TileMeshClientConfig {
  width: number;
  depth: number;
  resolution: number;
  /** Encoded as string so 64-bit seeds round-trip cleanly to a future worker via postMessage. */
  seed: string;
  source: MeshHeightmapSource | MeshNoiseSource;
  /**
   * Terrain hole polygons baked at init. Each `{vertices, aabb}` postMessage-
   * clones cleanly. Editor authoring uses [updateHoles] for live changes;
   * playful sets this once and never mutates.
   */
  holes?: HolePolygon[];
}

/** Minimal interface so tests can inject a mock without a real Worker. */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: ArrayBuffer[]): void;
  onmessage: ((e: { data: unknown }) => void) | null;
  onerror: ((e: { message?: string }) => void) | null;
  terminate(): void;
}

type PendingRequest = { resolve: (out: TileMeshOutput) => void; reject: (err: Error) => void };

export class TileMeshClient {
  private meshConfig: TileMeshConfig | null = null;
  private sample: HeightSampleFn | null = null;
  private savedConfig: TileMeshClientConfig | null = null;
  /**
   * Main-thread reference to the `HeightmapHeightField` driving [sample].
   * Exposed so the editor's brush authoring path can mutate the sculpt grid
   * in place without rebuilding the streamer. Null when the source isn't a
   * heightmap (noise fallback) or when [useWorker] has handed off sampling.
   */
  private heightField: HeightmapHeightField | null = null;
  /**
   * Terrain holes installed via [updateHoles]. Forwarded to every
   * `generateTileMesh` call so per-tile meshes skip quads in holes.
   */
  private holes: readonly HolePolygon[] | null = null;

  private worker: WorkerLike | null = null;
  private nextReqId = 0;
  private pending = new Map<number, PendingRequest>();

  /** Initialise with terrain config + height source. Must be called before generate(). */
  init(config: TileMeshClientConfig): void {
    if (this.meshConfig) {
      throw new Error('TileMeshClient.init called twice');
    }
    this.savedConfig = config;
    const seed = parseSeed(config.seed);
    this.meshConfig = {
      width: config.width,
      depth: config.depth,
      resolution: config.resolution,
      seed,
    };
    const built = buildSamplerWithField(config.source, seed);
    this.sample = built.sample;
    this.heightField = built.field;
    this.holes = config.holes && config.holes.length > 0 ? config.holes : null;
  }

  /**
   * Install or replace the sculpt-delta grid on the active height field.
   * No-op when the source isn't a heightmap (noise mode) or when a Web
   * Worker is handling sampling (the worker keeps its own field). Returns
   * the field reference so the caller can mutate the underlying pixel
   * buffer in place between subsequent `generate` calls.
   */
  updateSculpt(grid: HeightmapGrid | null, amplitude: number): HeightmapHeightField | null {
    this.heightField?.setSculptPatch(grid, amplitude);
    return this.heightField;
  }

  /**
   * Replace the active terrain-hole list. Subsequent `generate` calls skip
   * quads inside any of these. Pass `null` (or empty array) to clear.
   */
  updateHoles(holes: readonly HolePolygon[] | null): void {
    this.holes = holes && holes.length > 0 ? holes : null;
    // Also stash in savedConfig so a subsequent `useWorker` init message
    // carries the holes to the worker. Without this, holes set via
    // `updateHoles` (e.g. from the streamer constructor, AFTER `init`) get
    // dropped on the worker handoff and tiles render without skipping.
    if (this.savedConfig) {
      this.savedConfig = { ...this.savedConfig, holes: this.holes ? [...this.holes] : undefined };
    }
  }

  /**
   * Wire a Web Worker for off-thread tile generation. Must be called after
   * `init()`. The worker should be a shim that delegates to `TerrainGenWorker.handleTerrainGenMessage`.
   * An `init` message is sent immediately to seed the worker with the terrain config.
   */
  useWorker(worker: WorkerLike): void {
    if (!this.savedConfig) throw new Error('TileMeshClient.useWorker called before init');
    this.worker = worker;

    worker.onmessage = (e: { data: unknown }) => {
      const msg = e.data as { reqId: number; type: string } & Record<string, unknown>;
      if (msg.type === 'initOk') return;
      if (msg.type === 'generateResult') {
        const entry = this.pending.get(msg.reqId);
        if (!entry) return;
        this.pending.delete(msg.reqId);
        const r = msg as unknown as TerrainWorkerGenerateResult;
        entry.resolve({
          positions: r.positions,
          normals: r.normals,
          uvs: r.uvs,
          indices: r.indices,
          vertexCount: r.vertexCount,
          triangleCount: r.triangleCount,
        });
      }
    };
    worker.onerror = (e: { message?: string }) => {
      const err = new Error(e.message ?? 'TerrainGenWorker error');
      for (const entry of this.pending.values()) entry.reject(err);
      this.pending.clear();
    };

    // Send init; heightmap pixels are structured-cloned (not transferred) so
    // the main thread retains them for height-oracle queries.
    const initMsg: TerrainWorkerMessage = {
      reqId: this.nextReqId++,
      type: 'init',
      config: this.savedConfig,
    };
    worker.postMessage(initMsg, []);
  }

  /**
   * Request mesh generation for one tile/LOD. Delegates to the worker when
   * available; falls back to a synchronous call on the main thread otherwise.
   */
  generate(bounds: TileBounds, lodLevel: number): Promise<TileMeshOutput> {
    if (!this.meshConfig || !this.sample) {
      return Promise.reject(new Error('TileMeshClient.generate before init'));
    }
    if (this.worker) {
      return new Promise((resolve, reject) => {
        const reqId = this.nextReqId++;
        this.pending.set(reqId, { resolve, reject });
        const msg: TerrainWorkerMessage = { reqId, type: 'generate', bounds, lodLevel };
        this.worker!.postMessage(msg, []);
      });
    }
    try {
      const out = generateTileMesh(
        this.meshConfig!,
        bounds,
        this.sample!,
        lodLevel,
        this.holes ?? undefined,
      );
      return Promise.resolve(out);
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  destroy(): void {
    this.worker?.terminate();
    const err = new Error('TileMeshClient destroyed');
    for (const entry of this.pending.values()) entry.reject(err);
    this.pending.clear();
    this.worker = null;
    this.meshConfig = null;
    this.sample = null;
    this.savedConfig = null;
  }
}

function buildSamplerWithField(
  source: MeshHeightmapSource | MeshNoiseSource,
  seed: number | bigint,
): { sample: HeightSampleFn; field: HeightmapHeightField | null } {
  if (source.kind === 'heightmap') {
    // Raw bilinear (cellSize: 0) so analytic central differences are continuous
    // across tile boundaries.
    const sculptPatch =
      source.sculptPixels && source.sculptWidth && source.sculptHeight
        ? {
            data: source.sculptPixels,
            width: source.sculptWidth,
            height: source.sculptHeight,
          }
        : undefined;
    const field = new HeightmapHeightField({
      heightmap: { data: source.pixels, width: source.width, height: source.height },
      amplitude: source.amplitude,
      worldMinX: source.worldMinX,
      worldMinZ: source.worldMinZ,
      worldWidth: source.worldWidth,
      worldDepth: source.worldDepth,
      cellSize: 0,
      sculptPatch,
      sculptAmplitude: source.sculptAmplitude ?? 0,
    });
    return { sample: (x, z) => field.sampleHeight(x, z) ?? 0, field };
  }
  const noise = new NoiseHeightField({
    seed,
    octaves: source.octaves,
    baseFrequency: source.baseFrequency,
    amplitude: source.amplitude,
    worldWidth: source.worldWidth,
    worldDepth: source.worldDepth,
    falloffShape: source.falloffShape,
  });
  return { sample: (x, z) => noise.sampleHeight(x, z) ?? 0, field: null };
}

function parseSeed(s: string): number | bigint {
  const n = Number(s);
  if (Number.isSafeInteger(n)) return n;
  try {
    return BigInt(s);
  } catch {
    return 0;
  }
}
